import { AppInputProps } from '@/types/common';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRIES } from '@/utils/countries';

import commonUtils from '@/utils/common';

const ORGANIZATION_NAME_PATTERN = /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/;
const ORGANIZATION_NAME_MAX_LENGTH = 70;

const UseOrganizationEditor = () => {
  const { t } = useTranslation();

  const getSchoolNameValidation = (value: string): { isValid: boolean; error: string } => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return { isValid: false, error: '' };
    }

    const isTooLong = trimmedValue.length > ORGANIZATION_NAME_MAX_LENGTH;
    const isValid = !isTooLong && ORGANIZATION_NAME_PATTERN.test(trimmedValue);

    return {
      isValid,
      error: isValid ? '' : t(isTooLong ? 'organizationNameTooLong' : 'invalidOrganizationName'),
    };
  };

  const onInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = target;

    setOrganizationDetailsFields(prevState => {
      const updatedState = [...prevState];

      const fieldIndex = updatedState.findIndex(field => field.name === name);

      if (name === 'termsAndConditions') {
        updatedState[fieldIndex].value =
          updatedState[fieldIndex].value === 'false' ? 'true' : 'false';
        updatedState[fieldIndex].isValid = false;
        updatedState[fieldIndex].error = '';
      } else {
        updatedState[fieldIndex].value = value;

        if (updatedState[fieldIndex].id === 'school_name') {
          const { isValid, error } = getSchoolNameValidation(value);
          updatedState[fieldIndex].isValid = isValid;
          updatedState[fieldIndex].error = error;
        } else {
          updatedState[fieldIndex].isValid = false;
          updatedState[fieldIndex].error = '';
        }
      }

      return updatedState;
    });
  };

  const onSelect = (name: string, value: string, label?: string) => {
    setOrganizationDetailsFields(prevState => {
      const updatedState = [...prevState];

      const fieldIndex = updatedState.findIndex(field => field.name === name);
      const cityFieldIndex = updatedState.findIndex(field => field.name === 'city');

      if (name === 'state') {
        updatedState[cityFieldIndex].value = '';
        updatedState[cityFieldIndex].label = '';
        updatedState[cityFieldIndex].isValid = false;
        updatedState[cityFieldIndex].error = '';
      }

      updatedState[fieldIndex].value = value;
      updatedState[fieldIndex].label = label;
      updatedState[fieldIndex].isValid = false;
      updatedState[fieldIndex].error = '';

      return updatedState;
    });
  };

  const onOrganizationDetailsSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setOrganizationDetailsFields(prevState => {
      const updatedState = [...prevState];

      for (let i = 0; i < updatedState.length; i++) {
        const { id, value } = updatedState[i];

        const isSchoolNameField = id === 'school_name';
        const isStateField = id === 'state';
        const isCityField = id === 'city';

        if (isCityField) {
          updatedState[i].isValid = true;
          continue;
        }

        if (!value) continue;

        if (isSchoolNameField) {
          const { isValid, error } = getSchoolNameValidation(value);

          updatedState[i].isValid = isValid;

          if (!isValid) {
            updatedState[i].error = error;
          }
        } else if (isStateField) {
          const trimmedValue = value.trim();
          const isValid = Boolean(trimmedValue) && trimmedValue.length <= 40;

          updatedState[i].isValid = isValid;

          if (!isValid) {
            updatedState[i].error = t('invalidField');
          }
        } else if (isCityField) {
          updatedState[i].isValid = true;
        } else if (id === 'terms_and_conditions') {
          const isTermsAndConditionsValid = value === 'true';

          updatedState[i].isValid = isTermsAndConditionsValid;

          if (!isTermsAndConditionsValid) {
            updatedState[i].error = t('fieldIsMandatory');
          }
        }
      }

      return updatedState;
    });
  };

  const initialOrganizationDetailsFields: AppInputProps[] = [
    {
      id: 'school_name',
      name: 'schoolName',
      label: 'schoolName',
      placeholder: 'schoolName',
      value: '',
      error: '',
      isRequired: true,
      isValid: false,
      emitChange: onInputChange,
    },
    {
      id: 'state',
      name: 'state',
      label: 'state',
      placeholder: 'state',
      value: '',
      error: '',
      isRequired: true,
      isValid: false,
      emitSelect: onSelect,
    },
    {
      id: 'city',
      name: 'city',
      label: 'city',
      placeholder: 'city',
      value: '',
      error: '',
      isRequired: false,
      isValid: false,
      emitSelect: onSelect,
    },
    {
      id: 'country',
      name: 'country',
      label: 'country',
      placeholder: '',
      value: '',
      error: '',
      isDisabled: true,
      countryFlagImg: '',
      isValid: false,
      emitSelect: onSelect,
    },
    {
      id: 'native_language_code',
      name: 'nativeLanguageCode',
      label: 'language',
      placeholder: '',
      value: '',
      error: '',
      isDisabled: true,
      hideValue: true,
      img: '',
      isValid: false,
      emitChange: onInputChange,
    },
    {
      id: 'terms_and_conditions',
      name: 'termsAndConditions',
      type: 'checkbox',
      value: 'false',
      error: '',
      isRequired: true,
      isValid: false,
      emitChange: onInputChange,
    },
  ];

  const [organizationDetailsFields, setOrganizationDetailsFields] = useState<AppInputProps[]>(
    initialOrganizationDetailsFields
  );

  const onUpdateOrganizationDetailsFields = (country: string, nativeLanguageCode: string) => {
    setOrganizationDetailsFields(prevState => {
      const updatedState = [...prevState];

      const countryIndex = updatedState.findIndex(field => field.id === 'country');
      const nativeLanguageCodeIndex = updatedState.findIndex(
        field => field.id === 'native_language_code'
      );

      const countryObj = COUNTRIES.find(
        _country => _country.name.toLowerCase() === country.toLowerCase()
      );

      updatedState[countryIndex].value = country;
      updatedState[countryIndex].isValid = true;
      updatedState[countryIndex].countryFlagImg = countryObj?.code.toLocaleLowerCase();

      updatedState[nativeLanguageCodeIndex].value = nativeLanguageCode;
      updatedState[nativeLanguageCodeIndex].isValid = true;
      updatedState[nativeLanguageCodeIndex].placeholder =
        t(`languages.${nativeLanguageCode}`) ?? '';
      updatedState[nativeLanguageCodeIndex].img = commonUtils.getLanguageIcon(nativeLanguageCode);

      return updatedState;
    });
  };

  return {
    organizationDetailsFields,
    onUpdateOrganizationDetailsFields,
    onOrganizationDetailsSubmit,
  };
};

export default UseOrganizationEditor;
